import AccountCircle from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import InstagramIcon from "@mui/icons-material/Instagram";
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Link,
} from "@mui/material";
import React, { useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { isAdmin, isAuthenticated } from "../utils/auth";
import TheShelfSagaLogoWide from "../images/TheShelfSagaLogoWide.png";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    handleClose();
    navigate("/login");
  };

  const handleLogin = () => {
    navigate("/login");
  };

  const menuItems = [
    { path: "/", label: "Home" },
    { path: "/calendar", label: "Calendar" },
    { path: "/blog", label: "Blog" },
  ];

  const renderMenuItems = () => (
    <>
      {menuItems.map((item) => (
        <Button
          key={item.path}
          color="inherit"
          component={RouterLink}
          to={item.path}
          sx={{
            fontSize: "1.1rem",
            fontWeight: location.pathname === item.path ? "bold" : "normal",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              width: location.pathname === item.path ? "100%" : "0%",
              height: "2.5px",
              bottom: 0,
              left: 0,
              backgroundColor:
                location.pathname === item.path
                  ? "#D4AF37"
                  : "primary.contrastText",
              transition: "width 0.3s ease, background-color 0.3s ease",
            },
            "&:hover::after": {
              width: "100%",
            },
          }}
        >
          {item.label}
        </Button>
      ))}
    </>
  );

  const renderMobileMenu = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={handleMobileMenu}
      PaperProps={{
        sx: {
          width: 240,
          backgroundColor: "primary.main",
          color: "primary.contrastText",
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Menu
        </Typography>
        <Divider sx={{ bgcolor: "primary.contrastText", mb: 2 }} />
        <List>
          {menuItems.map((item) => (
            <ListItem
              key={item.path}
              button
              component={RouterLink}
              to={item.path}
              onClick={handleMobileMenu}
              sx={{
                fontWeight: location.pathname === item.path ? "bold" : "normal",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              <ListItemText primary={item.label} />
            </ListItem>
          ))}
          <Divider sx={{ bgcolor: "primary.contrastText", my: 1 }} />
          <ListItem
            button
            component="a"
            href="https://instagram.com/theshelfsaga"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleMobileMenu}
            sx={{
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            <ListItemText primary="Instagram" />
          </ListItem>
          {isAuthenticated() ? (
            <>
              {isAdmin() && (
                <>
                  <Divider sx={{ bgcolor: "primary.contrastText", my: 1 }} />
                  <ListItem
                    button
                    component={RouterLink}
                    to="/publishers/new"
                    onClick={handleMobileMenu}
                    sx={{
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    }}
                  >
                    <ListItemText primary="New Publisher" />
                  </ListItem>
                  <ListItem
                    button
                    component={RouterLink}
                    to="/book-releases/new"
                    onClick={handleMobileMenu}
                    sx={{
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    }}
                  >
                    <ListItemText primary="New Release" />
                  </ListItem>
                  <ListItem
                    button
                    component={RouterLink}
                    to="/admin/users"
                    onClick={handleMobileMenu}
                    sx={{
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    }}
                  >
                    <ListItemText primary="User Management" />
                  </ListItem>
                </>
              )}
              <Divider sx={{ bgcolor: "primary.contrastText", my: 1 }} />
              <ListItem
                button
                onClick={() => {
                  handleLogout();
                  handleMobileMenu();
                }}
                sx={{
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
              >
                <ListItemText primary="Logout" />
              </ListItem>
            </>
          ) : (
            <ListItem
              button
              onClick={() => {
                handleLogin();
                handleMobileMenu();
              }}
              sx={{
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              <ListItemText primary="Login" />
            </ListItem>
          )}
        </List>
      </Box>
    </Drawer>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 50%, ${theme.palette.primary.dark} 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: "relative",
          minHeight: 80, // Fixed minimum height
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(ellipse at top, rgba(201, 169, 97, 0.1) 0%, transparent 50%)",
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A961' fill-opacity='0.05'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3Ccircle cx='37' cy='23' r='1'/%3E%3Ccircle cx='53' cy='53' r='1'/%3E%3Ccircle cx='23' cy='37' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
            opacity: 0.6,
            pointerEvents: "none",
          },
        }}
      >
        <Toolbar
          sx={{
            position: "relative",
            zIndex: 2,
            minHeight: 80, // Match AppBar height
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              marginRight: 2,
              marginLeft: { xs: 2, sm: 6 },
              height: 80, // Fixed height to prevent layout shift
              minWidth: 0, // Prevent flex item overflow
            }}
          >
            <Link
              component={RouterLink}
              to="/"
              sx={{
                height: "70%", // Consistent logo size
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            >
              <img
                src={TheShelfSagaLogoWide}
                alt="The Shelf Saga Logo"
                style={{
                  height: "100%",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </Link>
          </Box>

          {isMobile ? (
            <IconButton
              color="inherit"
              onClick={handleMobileMenu}
              sx={{ ml: 2 }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {renderMenuItems()}
              <Box
                sx={{
                  flexGrow: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Link
                  href="https://instagram.com/theshelfsaga"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "inherit",
                    display: "flex",
                    alignItems: "center",
                    "&:hover": {
                      color: "#D4AF37",
                      transform: "scale(1.1)",
                      transition: "transform 0.2s ease, color 0.2s ease",
                    },
                  }}
                >
                  <InstagramIcon />
                </Link>
                {isAuthenticated() ? (
                  <>
                    <IconButton
                      size="large"
                      aria-label="account of current user"
                      aria-controls="menu-appbar"
                      aria-haspopup="true"
                      onClick={handleMenu}
                      color="inherit"
                      sx={{
                        "&:hover": {
                          transform: "scale(1.1)",
                          transition: "transform 0.2s ease",
                        },
                      }}
                    >
                      <AccountCircle />
                    </IconButton>
                    <Menu
                      id="menu-appbar"
                      anchorEl={anchorEl}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                      PaperProps={{
                        sx: {
                          mt: 1.5,
                          backgroundColor: "primary.main",
                          color: "primary.contrastText",
                          "& .MuiMenuItem-root": {
                            "&:hover": {
                              backgroundColor: "primary.dark",
                            },
                          },
                        },
                      }}
                    >
                      {isAdmin() && (
                        <>
                          <MenuItem
                            component={RouterLink}
                            to="/publishers/new"
                            onClick={handleClose}
                          >
                            New Publisher
                          </MenuItem>
                          <MenuItem
                            component={RouterLink}
                            to="/book-releases/new"
                            onClick={handleClose}
                          >
                            New Release
                          </MenuItem>
                          <MenuItem
                            component={RouterLink}
                            to="/admin/users"
                            onClick={handleClose}
                          >
                            User Management
                          </MenuItem>
                        </>
                      )}
                      <MenuItem onClick={handleLogout}>Logout</MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Button
                    color="inherit"
                    onClick={handleLogin}
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight:
                        location.pathname === "/login" ? "bold" : "normal",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        width: location.pathname === "/login" ? "100%" : "0%",
                        height: "2.5px",
                        bottom: 0,
                        left: 0,
                        backgroundColor:
                          location.pathname === "/login"
                            ? "#D4AF37"
                            : "primary.contrastText",
                        transition:
                          "width 0.3s ease, background-color 0.3s ease",
                      },
                      "&:hover::after": {
                        width: "100%",
                      },
                    }}
                  >
                    Login
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      {renderMobileMenu()}
      <Container
        component="main"
        disableGutters
        maxWidth={false}
        sx={{
          flex: 1,
          py: { xs: 0, sm: 0, md: 4 },
          px: { xs: 0, sm: 0, md: 2 },
          width: "100%",
        }}
      >
        {children}
      </Container>
    </Box>
  );
};

export default Layout;
